import { ChangeDetectorRef, Component } from '@angular/core'
import { CommonModule, NgOptimizedImage } from '@angular/common'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest, filter, map, Observable, take } from 'rxjs'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { FormulaService } from '@shared/services/formula.service'
import { LibraryDeliverableEntryDetailsService } from '@app/features/library-management/services/library-deliverable-entry-details.service'
import { ScopeConfigurationService } from '@app/features/scope-overview/service/scope-configuration.service'
import { ScopeOverviewNavigationService } from '@app/features/scope-overview/service/scope-overview-navigation.service'
import { ScopeOverviewTab } from '@app/features/scope-overview/enum/scope-overview-tab.enum'
import { ChangeRequestActions } from '@app/features/scope-overview/change-request/store/actions/change-request.actions'
import { ChangeRequestSelectors } from '@app/features/scope-overview/change-request/store/selectors/change-request.selectors'
import { ChangeRequest, ChangeRequestStatus } from '@app/core/model/change-request.model'
import { ChangeItemType } from '@app/features/scope-overview/change-request/model/change-item-type.enum'
import { ScenarioCategory } from '@app/features/scope-overview/model/scenario-category.model'
import { ScopeConfigurationComponent } from './scope-configuration.component'
import { ScopeUiCheckboxComponent } from '@shared/components/ui-components/scope-ui-checkbox/scope-ui-checkbox.component'
import { ScopeUiDatepickerComponent } from '@shared/components/ui-components/scope-ui-datepicker/scope-ui-datepicker.component'
import { ScopeUiDropdownComponent } from '@shared/components/ui-components/scope-ui-dropdown/scope-ui-dropdown.component'
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component'
import { FormulaBuilderComponent } from '@shared/components/formula-builder/formula-builder.component'
import { SharedModule } from '@shared/shared.module'
import { LibraryManagementNgrxModule } from '@app/features/library-management/library-management-ngrx.module'
import { Scenario } from '../../model/scenario.model'
import { ScopeConfiguration, ScopeVersion } from '@app/core/model/scope-version'
import { mapScenario } from '../../utils/map-scenario-util.const'
import { isNil, isNumber } from 'lodash'
import { getConfigurationWithoutAddons } from '../../utils/get-configuration-without-addons.util'

@Component({
  selector: 'scope-configuration-cr',
  templateUrl: './scope-configuration.component.html',
  styleUrls: ['./scope-configuration.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ScopeUiInputComponent,
    FormulaBuilderComponent,
    ScopeUiDatepickerComponent,
    ScopeUiDropdownComponent,
    NgOptimizedImage,
    LibraryManagementNgrxModule,
    ScopeUiCheckboxComponent,
  ],
})
export class ScopeConfigurationCRComponent extends ScopeConfigurationComponent {
  changeItemId?: string

  private crSaveLoading$ = new BehaviorSubject<boolean>(false)
  private crSubmitLoading$ = new BehaviorSubject<boolean>(false)
  override saveLoading$ = this.crSaveLoading$.asObservable()
  override submitLoading$ = this.crSubmitLoading$.asObservable()

  constructor(
    store: Store,
    cdr: ChangeDetectorRef,
    formulaService: FormulaService,
    approvalFlowService: ApprovalFlowService,
    libraryDeliverableEntryDetailsService: LibraryDeliverableEntryDetailsService,
    private actions$: Actions,
    private scopeConfigurationService: ScopeConfigurationService,
    private scopeOverviewNavigationService: ScopeOverviewNavigationService
  ) {
    super(store, cdr, formulaService, approvalFlowService, libraryDeliverableEntryDetailsService)
  }

  override ngOnInit() {
    super.ngOnInit()

    combineLatest([
      this.store.select(ChangeRequestSelectors.selectCurrentChangeRequest),
      this.store.select(ChangeRequestSelectors.changeRequestAbleToMerge),
    ])
      .pipe(this.destroy$())
      .subscribe(([changeRequest, ableToMerge]: [ChangeRequest | undefined, boolean]) => {
        if (this.changeRequestId !== changeRequest?.id) {
          this.changeRequestId = changeRequest?.id
          this.editChangeRequestByScenarioAgree = false
          this.changeItemId = this.getScenarioConfigChange(changeRequest)?.uuid
          this.scenarioInitialized = false
        }

        this.editable = ableToMerge && changeRequest?.status === ChangeRequestStatus.DRAFT
      })
  }

  override getFormData() {
    combineLatest([this.scenario$, this.configuration$, this.store.select(ChangeRequestSelectors.selectPreviewScope)])
      .pipe(
        this.destroy$(),
        filter(([scenario, _configuration, previewScope]) => !!scenario && !!previewScope),
        take(1)
      )
      .subscribe(([scenario, configuration, previewScope]: [Scenario, ScopeConfiguration, ScopeVersion]) => {
        this.configuration = previewScope?.configuration || configuration || {}
        this.scenario = mapScenario(scenario, this.configuration)
        this.initializeScenario()
      })
  }

  private getScenarioConfigChange(changeRequest?: ChangeRequest) {
    return changeRequest?.changes?.find((change) => change.type === ChangeItemType.SCENARIO_CONFIGURATION)
  }

  override updateConfiguration(configuration: ScopeConfiguration) {
    this.store.dispatch(ChangeRequestActions.updateConfiguration({ configuration }))
  }

  override updateScenario(_scenario: Scenario) {
    return
  }

  override save(category: ScenarioCategory, selectNext?: boolean) {
    if (!this.changeRequestId) {
      super.save(category, selectNext)
      return
    }

    this.prepareSaveConfiguration(category, (nextCategoryId) => {
      this.crSaveLoading$.next(true)
      this.calculateComponentModifiers()
        .pipe(take(1))
        .subscribe((componentModifiers) => {
          this.saveConfigurationToChangeRequest(() => {
            this.crSaveLoading$.next(false)
            this.updateStatesAfterSaveConfiguration(category.id, nextCategoryId, selectNext)
            this.cdr.detectChanges()
          }, componentModifiers)
        })
    })
  }

  override submitConfiguration() {
    if (!this.changeRequestId) {
      super.submitConfiguration()
      return
    }

    this.crSubmitLoading$.next(true)
    this.calculateComponentModifiers()
      .pipe(take(1))
      .subscribe((componentModifiers) => {
        this.saveConfigurationToChangeRequest(
          () => {
            this.store.dispatch(
              ChangeRequestActions.generateChangesFromScenarioConfig({
                scopeId: this.scopeVersion.identity.id,
                changeRequestId: this.changeRequestId!,
              })
            )

            this.actions$
              .pipe(ofType(ChangeRequestActions.generateChangesFromScenarioConfigSuccess), take(1))
              .subscribe(() => {
                this.crSubmitLoading$.next(false)
                this.scopeOverviewNavigationService.selectTabAndReload(
                  ScopeOverviewTab.SCOPE,
                  this.scopeVersion.identity.id
                )
              })
          },
          componentModifiers,
          true
        )
      })
  }

  private calculateComponentModifiers(): Observable<Record<number, number>> {
    return this.scopeConfigurationService.getModifiers(this.scenario.id).pipe(
      map((modifiers) => {
        const result: Record<number, number> = { ...this.scopeVersion.componentModifierValues }
        modifiers.forEach((modifier) => {
          const calculated = this.formulaService.calculate(modifier.formula)
          if (isNumber(calculated) || isNil(calculated)) {
            result[modifier.id] = calculated
          }
        })
        return result
      })
    )
  }

  private saveConfigurationToChangeRequest(
    onSuccess?: () => void,
    componentModifiers?: Record<number, number>,
    removeConfigurationAddons?: boolean
  ): void {
    this.store.dispatch(
      ChangeRequestActions.saveScenarioConfig({
        scopeId: this.scopeVersion.identity.id,
        changeRequestId: this.changeRequestId!,
        configuration: removeConfigurationAddons
          ? getConfigurationWithoutAddons(this.scenario, this.configuration, this.formulaService)
          : this.configuration,
        componentModifiers: componentModifiers ?? {},
        changeItemId: this.changeItemId,
      })
    )

    this.actions$.pipe(ofType(ChangeRequestActions.saveScenarioConfigSuccess), take(1)).subscribe(({ changeItem }) => {
      this.updateChangeItemId(changeItem?.uuid)
      onSuccess?.()
    })

    this.actions$.pipe(ofType(ChangeRequestActions.saveScenarioConfigFail), take(1)).subscribe(() => {
      this.crSaveLoading$.next(false)
      this.crSubmitLoading$.next(false)
    })
  }

  private updateChangeItemId(uuid?: string): void {
    if (uuid && !this.changeItemId) this.changeItemId = uuid
  }
}
